import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { datadogLogs } from "@datadog/browser-logs";

/** What the API says about the token in an enrollment URL. */
export type EnrollmentTokenStatus = "Valid" | "Invalid" | "Expired" | "Completed";

/** How the debit amount is determined. Mirrors `AchAmountType` on the API. */
export type AchAmountType = "Fixed" | "Variable" | "VariableCapped";

/** The maximum cadence of new debits. Mirrors `AchMandateFrequency` on the API. */
export type AchMandateFrequency = "Weekly" | "BiWeekly" | "Monthly" | "Quarterly";

/** What ends the authorization. Mirrors `AchContractEndCondition` on the API. */
export type AchContractEndCondition = "OnCancel" | "OnEndDate" | "MaxDebits";

/** The mandate terms the customer is being asked to authorize. */
export interface EnrollmentTerms {
  amountType: AchAmountType;
  /** Fixed only. */
  amount: number | null;
  /** VariableCapped only. */
  maxAmount: number | null;
  frequency: AchMandateFrequency;
  /** Date only, no offset — e.g. `2026-11-01T00:00:00`. */
  startDate: string;
  /**
   * What ends the authorization. Null only for links created before it was captured; the end is
   * then described from `endDate` and `maxDebits`.
   */
  contractEndCondition: AchContractEndCondition | null;
  /** Date only, no offset. Null when the authorization has no end date. */
  endDate: string | null;
  /** Null when the number of debits is unlimited. */
  maxDebits: number | null;
  currency: string;
}

/** The dealer details printed on the consent. Each is null only for links created before it was captured. */
export interface EnrollmentDealer {
  dealerName: string | null;
  dealerPhone: string | null;
  paymentReceiverName: string | null;
}

export interface ValidateEnrollmentTokenRequest {
  token: string;
}

/** What the bank-account step needs to mount Adyen's ACH component. */
export interface EnrollmentAdyenConfig {
  clientKey: string;
  /** `test` or `live`. */
  environment: string;
}

export type BankAccountType = "checking" | "savings";
export type AccountHolderType = "personal" | "business";

/**
 * A bank account the customer saved on an earlier enrollment, offered for reuse (182095). Only what the
 * customer can already see: no bank name, no holder name, no token.
 */
export interface EnrollmentSavedAccount {
  id: string;
  accountType: BankAccountType;
  accountHolderType: AccountHolderType;
  /** May be empty when Adyen did not report the last four digits. */
  last4: string;
}

export interface ValidateEnrollmentTokenResponse {
  status: EnrollmentTokenStatus;
  /** UTC, with offset. Populated only when Valid. */
  expiresAt: string | null;
  /** Populated only when Valid. */
  terms: EnrollmentTerms | null;
  /** Populated only when Valid. */
  dealer: EnrollmentDealer | null;
  /** Populated only when Valid; null if the server has no Adyen client key configured. */
  adyen: EnrollmentAdyenConfig | null;
  /** The customer's reusable saved accounts, newest first (182095). Empty unless Valid. */
  savedAccounts: EnrollmentSavedAccount[];
}

/** Adyen's encrypted bank details, exactly as its ACH component produced them. */
export interface EnrollmentBankDetails {
  encryptedBankAccountNumber: string;
  encryptedBankLocationId: string;
  ownerName: string;
}

interface SignMandateRequestBase {
  token: string;
  signature: string;
  consentAgreed: boolean;
}

/** Signing with a new bank account: Adyen verifies and saves it in the same call. */
export interface SignMandateWithNewAccountRequest extends SignMandateRequestBase {
  paymentMethod: EnrollmentBankDetails;
  bankAccountType: BankAccountType;
  accountHolderType: AccountHolderType;
  ccdAcknowledged: boolean;
}

/**
 * Signing with a saved account (182095). No bank details: the account was verified when it was first
 * saved, and its type and CCD acknowledgement come from the server.
 */
export interface SignMandateWithSavedAccountRequest extends SignMandateRequestBase {
  vaultedAccountId: string;
}

/** A new account or a saved one — the API rejects a request with both. */
export type SignMandateRequest = SignMandateWithNewAccountRequest | SignMandateWithSavedAccountRequest;

/** The outcome of signing. Every value has its own screen on the result step. */
export type SignMandateStatus =
  | "Completed"
  | "VerificationFailed"
  | "ReEnterDetails"
  | "LinkUnusable"
  | "TryAgain"
  | "SavedAccountUnavailable";

export interface SignMandateResponse {
  status: SignMandateStatus;
  /** Populated only when LinkUnusable. */
  linkStatus: EnrollmentTokenStatus | null;
  /** Populated only when Completed. */
  account: { accountType: BankAccountType; accountHolderType: AccountHolderType; last4: string } | null;
  /** UTC, with offset. Populated only when Completed. */
  signedAt: string | null;
}

/**
 * Base query for the public ACH enrollment page.
 *
 * @remarks
 * Deliberately NOT the shared `baseQueryWithRefreshTokenHandler`. That one attaches the logged-in
 * user's JWT or API key, and on a 401 redirects to `/login` — both wrong here. The customer is
 * anonymous, the enrollment token is their only credential, and it travels in the request body.
 * `credentials: "omit"` keeps any PerseusPay cookies a dealer user might have in the same browser
 * off these calls.
 */
const publicBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_URL,
  credentials: "omit",
  prepareHeaders: (headers) => {
    headers.set("Content-Type", "application/json");
    headers.set("Accept", "application/json");
    return headers;
  },
});

/**
 * Logs failed calls the same way `authFetchBaseQuery` does, minus the response body. The request
 * body carries the token, so nothing from either side of the exchange is logged — only the endpoint
 * and status.
 */
const publicBaseQueryWithLogging: typeof publicBaseQuery = async (args, api, extraOptions) => {
  const result = await publicBaseQuery(args, api, extraOptions);
  if (result.error) {
    datadogLogs.logger.error("Public API Error", {
      httpStatus: result.error.status,
      endpoint: typeof args === "string" ? args : args.url,
      method: typeof args === "string" ? "GET" : args.method ?? "GET",
    });
  }
  return result;
};

/**
 * RTK Query slice for the anonymous endpoints behind the public ACH enrollment page
 * (`ACHRegistrationController`, anonymous as a whole).
 *
 * @remarks
 * Excluded from redux-persist in `app/store.ts`: query args include the token, and responses carry
 * the mandate terms — neither should outlive the tab in localStorage, least of all on a shared
 * computer.
 */
export const achEnrollmentPublicApi = createApi({
  reducerPath: "achEnrollmentPublicApi",
  baseQuery: publicBaseQueryWithLogging,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,

  endpoints: (builder) => ({
    /**
     * Checks the token from an enrollment URL before the page loads.
     *
     * @remarks
     * A POST used as a query, like `validateSignature`: the token goes in the body so it stays out of
     * URL-based telemetry. Every outcome comes back as HTTP 200 with a `status`; only a network or
     * server failure surfaces as `error`.
     */
    validateEnrollmentToken: builder.query<ValidateEnrollmentTokenResponse, ValidateEnrollmentTokenRequest>({
      query: (body) => ({
        url: "ACHRegistration/ValidateToken",
        method: "POST",
        body,
      }),
    }),

    /**
     * The customer signs. The server verifies and saves the bank account in the same call, and the
     * authorization only takes effect if that succeeds.
     *
     * @remarks
     * A mutation, so nothing about it is cached: the body holds the encrypted bank details, which
     * are single-use and must not be replayed.
     */
    signMandate: builder.mutation<SignMandateResponse, SignMandateRequest>({
      query: (body) => ({
        url: "ACHRegistration/SignMandate",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const { useValidateEnrollmentTokenQuery, useSignMandateMutation } = achEnrollmentPublicApi;
